<script setup lang="ts">
import { ref, computed } from 'vue';
import {
	IonContent,
	IonButton,
	IonFooter,
	IonIcon,
	IonInfiniteScroll,
	IonInfiniteScrollContent,
	IonSearchbar,
	IonSpinner,
	InfiniteScrollCustomEvent,
	IonRefresher,
	IonRefresherContent,
	RefresherCustomEvent,
} from '@ionic/vue';
import {
	search,
	chevronBack
} from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import useQueue from '@/modules/queue/composables/useQueue';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import { useAuthStore } from '@/stores/auth/authStore';

// Init
const { t } = useI18n();
const router = useRouter();
const queueSearch = ref('');
const activeTab = ref<'activities' | 'my-queues'>('activities');
const assetStore = useAssetsStore();
const authStore = useAuthStore();
const { loadImage } = useCacheImage();
const logoUrl = ref(assetStore.logoUrl);

// Load logo
if (assetStore.logoUrl) {
	let logoImage = assetStore.logoUrl.split('/').pop();
	logoImage = logoImage ?? 'logo';
	loadImage('logo', logoImage, assetStore.logoUrl).then(url => logoUrl.value = url);
}

// Queue composable
const { getQueueActivities, getMyQueues } = useQueue();
const { activities, fetchNextPage, hasNextPage, isFetchingActivitiesDataRequests, refetchActivitiesDataRequests } =
	getQueueActivities(queueSearch);
const { myQueues, isFetchingMyQueues, refetchMyQueues } = getMyQueues();

// Computed queue count
const myQueuesCount = computed(() => myQueues.value?.length || 0);

// Infinite scroll
const ionInfinite = async (ev: InfiniteScrollCustomEvent) => {
	if (hasNextPage && hasNextPage.value) {
		await fetchNextPage();
	}
	ev.target.complete();
};

// Pull to refresh
const handleRefresh = async (event: RefresherCustomEvent) => {
	if (activeTab.value === 'activities') {
		await refetchActivitiesDataRequests();
	} else {
		await refetchMyQueues();
	}
	event.target.complete();
};

// Navigate to category activities
const goToCategory = (categoryId: number, categoryName: string) => {
	router.push({
		name: 'queue-category-activities',
		params: { categoryId, categoryName }
	});
};

// Navigate to queue ticket detail
const goToQueueTicket = (queueSlotId: number) => {
	router.push(`/my-queues/${queueSlotId}`);
};
</script>

<template>
	<ion-content class="queue-activities-page">
		<ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>

		<!-- Fixed Header -->
		<div class="fixed-header">
			<!-- Logo -->
			<div class="flex items-center justify-center pt-16 mb-4">
				<img :src="logoUrl" class="max-w-[180px]" />
			</div>

			<!-- Tabs -->
			<div v-if="authStore.isAuthenticated()" class="flex items-center justify-center mb-6 px-5">
				<ion-button class="mr-2 w-[140px] h-[40px] button-solid rounded-full" :class="activeTab === 'activities' ? 'hover:bg-white' : 'backdrop-blur bg-white/20'" shape="round" :fill="activeTab === 'activities' ? 'default' : 'clear'" @click="activeTab = 'activities'">
					<span class="text-base2" :class="activeTab === 'activities' ? '' : 'text-white'">{{ t('queue.activities') }}</span>
				</ion-button>
				<ion-button
					class="w-[140px] h-[40px] button-solid rounded-full backdrop-blur bg-white/20 hover:bg-white/20"
					shape="round"
					fill="clear"
					@click="router.push('/my-queues')">
					<span class="text-base2 text-white">
						{{ t('queue.my_queues') }}
						<span v-if="myQueuesCount > 0"> ({{ myQueuesCount }})</span>
					</span>
				</ion-button>
			</div>

			<!-- Search Bar (only show on activities tab) -->
			<div v-if="activeTab === 'activities'" class="search-container">
				<ion-searchbar v-model="queueSearch" :placeholder="t('queue.search_activities')" class="custom-searchbar" :debounce="300" show-clear-button="focus"> </ion-searchbar>
			</div>
		</div>

		<!-- Content with padding for fixed header -->
		<div class="content-container">
			<!-- Activities Tab Content -->
			<div v-if="activeTab === 'activities'">
				<!-- Categories List -->
				<div v-if="activities && activities.length > 0" class="activities-list">
					<div
						v-for="category in activities"
						:key="category.id"
						class="activity-card"
						@click="goToCategory(category.id, category.name)">
						<div class="card-image-container">
							<img
								:src="category.image_url || '/api/placeholder/400/240'"
								:alt="category.name"
								class="card-image" />
							<div class="card-gradient-overlay"></div>
							<div class="card-content">
								<h3 class="card-title">{{ category.name }}</h3>
								<p v-if="category.short_description" class="card-subtitle">{{ category.short_description }}</p>
								<div v-else-if="category.description" class="card-subtitle" v-html="category.description"></div>
								<p v-else class="card-subtitle">{{ t('queue.explore_activities') }}</p>
								<div v-if="category.sub_activities_count > 0" class="activity-count">{{ category.sub_activities_count }} {{ category.sub_activities_count === 1 ? 'activity' : 'activities' }}</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Loading state -->
				<div v-else-if="isFetchingActivitiesDataRequests" class="loading-state">
					<ion-spinner name="circular"></ion-spinner>
				</div>

				<!-- Empty state -->
				<div v-else class="empty-state">
					<ion-icon :icon="search" class="empty-icon"></ion-icon>
					<h3 class="empty-title">
						{{ queueSearch ? t('queue.no_activities_found') : t('queue.no_activities_available') }}
					</h3>
					<p class="empty-subtitle">
						{{ queueSearch ? t('queue.try_different_search') : t('queue.check_back_later') }}
					</p>
				</div>
			</div>

			<!-- My Queues Tab Content -->
			<div v-else-if="activeTab === 'my-queues'">
				<!-- My Queues List -->
				<div v-if="myQueues && myQueues.length > 0" class="activities-list">
					<div
						v-for="queue in myQueues"
						:key="queue.id"
						class="queue-card"
						@click="goToQueueTicket(queue.id)">
						<div class="queue-card-content">
							<div class="queue-header">
								<h3 class="queue-title">{{ queue.queue_activity?.name || 'Queue Activity' }}</h3>
								<span class="queue-position">#{{ queue.position_number }}</span>
							</div>
							<div class="queue-info">
								<p class="queue-status">{{ t(`queue.status_${queue.status}`) }}</p>
								<p v-if="queue.booked_date" class="queue-date">
									{{ new Date(queue.booked_date).toLocaleDateString() }}
									<span v-if="queue.booked_time_slot">• {{ queue.booked_time_slot }}</span>
								</p>
							</div>
						</div>
					</div>
				</div>

				<!-- Loading state -->
				<div v-else-if="isFetchingMyQueues" class="loading-state">
					<ion-spinner name="circular"></ion-spinner>
				</div>

				<!-- Empty state -->
				<div v-else class="empty-state">
					<ion-icon :icon="search" class="empty-icon"></ion-icon>
					<h3 class="empty-title">{{ t('queue.no_queues_yet') }}</h3>
					<p class="empty-subtitle">{{ t('queue.browse_activities_to_join') }}</p>
					<ion-button class="mt-4" @click="activeTab = 'activities'">
						{{ t('queue.browse_activities') }}
					</ion-button>
				</div>
			</div>
		</div>

		<!-- Infinite scroll -->
		<ion-infinite-scroll @ionInfinite="ionInfinite">
			<ion-infinite-scroll-content></ion-infinite-scroll-content>
		</ion-infinite-scroll>

		<div class="h-[100px]">&nbsp;</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="queue" />
	</ion-footer>
</template>

<style lang="scss" scoped>
.queue-activities-page {
	--background: #0a0a0a;
}

// Fixed Header
.fixed-header {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	background: #0a0a0a;
	padding-bottom: 16px;
}

// Top Bar
.top-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px;
	padding-top: env(safe-area-inset-top, 16px);

	.back-button {
		--color: white;
		margin: 0;
		padding: 0;
		width: 40px;
		height: 40px;

		ion-icon {
			font-size: 24px;
		}
	}

	.page-title {
		color: white;
		font-size: 20px;
		font-weight: 600;
		font-family: 'SourceSansPro-SemiBold';
		margin: 0;
	}

	.spacer {
		width: 40px;
	}
}

// Search Container
.search-container {
	padding: 0 20px;

	.custom-searchbar {
		--background: rgba(255, 255, 255, 0.1);
		--color: white;
		--placeholder-color: rgba(255, 255, 255, 0.6);
		--icon-color: rgba(255, 255, 255, 0.6);
		--clear-button-color: white;
		--border-radius: 12px;
		padding: 0;

		&::part(native) {
			height: 44px;
		}
	}
}

// Content Container
.content-container {
	padding-top: 200px; // Space for fixed header with tabs
	padding-bottom: 100px; // Space for bottom navigation
	min-height: 100vh;
}

// Activities List
.activities-list {
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

// Activity Card
.activity-card {
	width: 100%;
	border-radius: 20px;
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.2s ease;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

	&:active {
		transform: scale(0.98);
	}

	.card-image-container {
		position: relative;
		width: 100%;
		height: 200px;

		.card-image {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.card-gradient-overlay {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 0) 0%,
				rgba(0, 0, 0, 0.2) 40%,
				rgba(0, 0, 0, 0.9) 100%
			);
		}

		.card-content {
			position: absolute;
			bottom: 0;
			left: 0;
			right: 0;
			padding: 20px;
			z-index: 2;

			.card-title {
				color: white;
				font-size: 24px;
				font-weight: 700;
				font-family: 'SourceSansPro-Bold';
				margin-bottom: 4px;
				line-height: 1.2;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}

			.card-subtitle {
				color: rgba(255, 255, 255, 0.9);
				font-size: 14px;
				font-weight: 400;
				font-family: 'SourceSansPro-Regular';
				line-height: 1.4;
				margin: 0 0 8px 0;
			}

			.activity-count {
				color: rgba(255, 255, 255, 0.7);
				font-size: 12px;
				font-weight: 500;
				font-family: 'SourceSansPro-SemiBold';
				text-transform: uppercase;
				letter-spacing: 0.5px;
				margin: 0;
			}
		}
	}
}

// Queue Card
.queue-card {
	width: 100%;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	cursor: pointer;
	transition: all 0.2s ease;
	overflow: hidden;

	&:active {
		transform: scale(0.98);
		background: rgba(255, 255, 255, 0.08);
	}

	.queue-card-content {
		padding: 20px;

		.queue-header {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			margin-bottom: 12px;

			.queue-title {
				color: white;
				font-size: 18px;
				font-weight: 600;
				font-family: 'SourceSansPro-SemiBold';
				margin: 0;
				flex: 1;
				line-height: 1.3;
			}

			.queue-position {
				color: rgba(255, 255, 255, 0.9);
				font-size: 20px;
				font-weight: 700;
				font-family: 'SourceSansPro-Bold';
				background: rgba(255, 255, 255, 0.1);
				padding: 4px 12px;
				border-radius: 12px;
				margin-left: 12px;
			}
		}

		.queue-info {
			.queue-status {
				color: rgba(255, 255, 255, 0.8);
				font-size: 14px;
				font-weight: 500;
				font-family: 'SourceSansPro-Regular';
				margin: 0 0 4px 0;
				text-transform: capitalize;
			}

			.queue-date {
				color: rgba(255, 255, 255, 0.6);
				font-size: 13px;
				font-family: 'SourceSansPro-Regular';
				margin: 0;
			}
		}
	}
}

// Loading State
.loading-state {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 400px;

	ion-spinner {
		--color: white;
		width: 48px;
		height: 48px;
	}
}

// Empty State
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	padding: 40px;
	text-align: center;

	.empty-icon {
		font-size: 64px;
		color: rgba(255, 255, 255, 0.3);
		margin-bottom: 20px;
	}

	.empty-title {
		color: white;
		font-size: 20px;
		font-weight: 600;
		font-family: 'SourceSansPro-SemiBold';
		margin-bottom: 8px;
	}

	.empty-subtitle {
		color: rgba(255, 255, 255, 0.6);
		font-size: 16px;
		font-family: 'SourceSansPro-Regular';
		margin: 0;
	}
}
</style>
